import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCategories, useServices } from '../hooks/useFirestoreServices';

export default function CategoryNavbar() {
  const { categories } = useCategories();
  const { services } = useServices();
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const handleMouseEnter = (categoryId: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setHoveredCategory(categoryId);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setHoveredCategory(null);
    }, 200);
  };

  // Get services for a specific category
  const getCategoryServices = (categoryId: string) => {
    return services.filter(service => service.categoryId === categoryId).slice(0, 6);
  };

  // Show first 8 categories
  const displayCategories = categories.filter(cat => cat.isActive !== false).slice(0, 8);

  if (displayCategories.length === 0) return null;

  return (
    <div className="bg-white border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center justify-center space-x-1 py-2">
          {displayCategories.map((category) => (
            <div
              key={category.id}
              className="relative"
              onMouseEnter={() => handleMouseEnter(category.id)}
              onMouseLeave={handleMouseLeave}
            >
              <Link
                to={`/services?category=${category.id}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 flex items-center gap-1 ${
                  hoveredCategory === category.id
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-primary-600'
                }`}
              >
                {category.icon && (
                  <span className="text-base">{category.icon}</span>
                )}
                <span className="whitespace-nowrap">{category.name}</span>
                <ChevronDown className={`h-3 w-3 transition-transform ${
                  hoveredCategory === category.id ? 'rotate-180' : ''
                }`} />
              </Link>

              {/* Dropdown Menu */}
              <AnimatePresence>
                {hoveredCategory === category.id && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-0 mt-1 w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50"
                    onMouseEnter={() => handleMouseEnter(category.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className="p-3">
                      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
                        {category.icon && (
                          <span className="text-xl">{category.icon}</span>
                        )}
                        <h3 className="font-bold text-gray-900 text-sm">{category.name}</h3>
                      </div>
                      
                      {getCategoryServices(category.id).length > 0 ? (
                        <div className="space-y-1">
                          {getCategoryServices(category.id).map((service) => (
                            <Link
                              key={service.id}
                              to={`/services/${service.id}`}
                              className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-gradient-to-r hover:from-primary-50 hover:to-purple-50 transition-all group"
                              onClick={() => setHoveredCategory(null)}
                            >
                              {/* Service Image */}
                              <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                                <img
                                  src={service.image}
                                  alt={service.name}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.src = 'https://via.placeholder.com/100?text=Service';
                                  }}
                                />
                              </div>
                              
                              {/* Service Name */}
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium text-gray-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                                  {service.name}
                                </span>
                              </div>
                            </Link>
                          ))}
                          
                          <Link
                            to={`/services?category=${category.id}`}
                            className="block mt-2 pt-2 border-t border-gray-100 text-center text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors py-1.5"
                            onClick={() => setHoveredCategory(null)}
                          >
                            View All {category.name} Services →
                          </Link>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 text-center py-3">
                          No services available
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
          
          {categories.length > 8 && (
            <Link
              to="/services"
              className="px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-100 rounded-lg transition-all duration-200"
            >
              View All
            </Link>
          )}
        </nav>

        {/* Mobile Navigation - Scrollable */}
        <nav className="md:hidden overflow-x-auto scrollbar-hide py-2">
          <div className="flex space-x-2">
            {displayCategories.map((category) => (
              <Link
                key={category.id}
                to={`/services?category=${category.id}`}
                className="flex-shrink-0 px-3 py-1.5 bg-white text-xs font-semibold text-gray-600 hover:bg-gray-100 hover:text-primary-600 rounded-full border border-gray-200 transition-all duration-200 flex items-center gap-1.5"
              >
                {category.icon && <span className="text-sm">{category.icon}</span>}
                <span className="whitespace-nowrap">{category.name}</span>
              </Link>
            ))}
            {categories.length > 8 && (
              <Link
                to="/services"
                className="flex-shrink-0 px-3 py-1.5 bg-primary-600 text-xs font-semibold text-white rounded-full transition-all duration-200"
              >
                View All
              </Link>
            )}
          </div>
        </nav>
      </div>
    </div>
  );
}
